import React from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Switch,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddIcon from '@mui/icons-material/Add';

import type { FacetGroup } from '@src/facets';
import type {
  CatalogRule,
  CatalogRuleAction,
  ConditionItem,
  LogicalOperator,
} from './types';
import { createEmptyCondition, createEmptyConditionGroup, generateId, isConditionGroup } from './types';
import ConditionRow from './ConditionRow';
import ConditionGroupCard from './ConditionGroupCard';
import ActionRow from './ActionRow';

interface RuleCardProps {
  rule: CatalogRule;
  index: number;
  facetGroups: FacetGroup[];
  onUpdate: (id: string, updates: Partial<CatalogRule>) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDragStart: (index: number) => void;
  onDrop: (targetIndex: number) => void;
  onDragEnd: () => void;
}

const RuleCard = ({ rule, index, facetGroups, onUpdate, onDelete, onDuplicate, onDragStart, onDrop, onDragEnd }: RuleCardProps) => {
  const handleConditionItemChange = (itemId: string, updates: Partial<ConditionItem>) => {
    onUpdate(rule.id, {
      conditions: rule.conditions.map(item =>
        item.id === itemId ? { ...item, ...updates } as ConditionItem : item,),
    });
  };

  const handleConditionItemDelete = (itemId: string) => {
    onUpdate(rule.id, {
      conditions: rule.conditions.filter(item => item.id !== itemId),
    });
  };

  const handleTopLevelOperatorChange = (newOperator: LogicalOperator) => {
    onUpdate(rule.id, {
      conditions: rule.conditions.map(item => ({ ...item, logicalOperator: newOperator })),
    });
  };

  const handleAddRule = () => {
    onUpdate(rule.id, {
      conditions: [...rule.conditions, createEmptyCondition()],
    });
  };

  const handleAddConditionGroup = () => {
    onUpdate(rule.id, {
      conditions: [...rule.conditions, createEmptyConditionGroup()],
    });
  };

  const handleActionChange = (actionId: string, updates: Partial<CatalogRuleAction>) => {
    onUpdate(rule.id, {
      actions: rule.actions.map(a =>
        a.id === actionId ? { ...a, ...updates } : a,),
    });
  };

  const handleActionDelete = (actionId: string) => {
    onUpdate(rule.id, {
      actions: rule.actions.filter(a => a.id !== actionId),
    });
  };

  const handleAddAction = () => {
    onUpdate(rule.id, {
      actions: [
        ...rule.actions,
        { id: generateId(), field: '', value: '', pairs: [] },
      ],
    });
  };

  return (
    <Paper
      variant="outlined"
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={e => e.preventDefault()}
      onDrop={() => onDrop(index)}
      onDragEnd={onDragEnd}
      sx={{ p: 3, mb: 3, cursor: 'default' }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box sx={{ color: 'text.disabled', cursor: 'grab', display: 'flex' }}>
            <DragIndicatorIcon />
          </Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Rule
            {' '}
            {index + 1}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Switch
            size="small"
            checked={rule.enabled}
            onChange={() => onUpdate(rule.id, { enabled: !rule.enabled })}
          />
          <IconButton size="small" onClick={() => onDuplicate(rule.id)} sx={{ color: 'text.secondary' }}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => onDelete(rule.id)} sx={{ color: 'text.secondary' }}>
            <DeleteOutlineIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ opacity: rule.enabled ? 1 : 0.4, pointerEvents: rule.enabled ? 'auto' : 'none' }}>

        {/* Conditions */}
        {rule.conditions.map((item, i) =>
        isConditionGroup(item) ? (
          <ConditionGroupCard
            key={item.id}
            group={item}
            isFirst={i === 0}
            depth={0}
            facetGroups={facetGroups}
            onChange={handleConditionItemChange}
            onDelete={handleConditionItemDelete}
            onSiblingOperatorChange={handleTopLevelOperatorChange}
          />
        ) : (
          <ConditionRow
            key={item.id}
            condition={item}
            isFirst={i === 0}
            facetGroups={facetGroups}
            onChange={handleConditionItemChange}
            onDelete={handleConditionItemDelete}
            onSiblingOperatorChange={handleTopLevelOperatorChange}
          />
        ),)}

        <Box display="flex" gap={1} mb={2}>
          <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddRule}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
            Add Condition
          </Button>
          <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddConditionGroup}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
            Add Nested Condition
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Actions */}
        {rule.actions.map(action => (
          <ActionRow
          key={action.id}
          action={action}
          facetGroups={facetGroups}
          onChange={handleActionChange}
          onDelete={handleActionDelete}
        />
      ))}

        <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={handleAddAction}
        sx={{ textTransform: 'none', fontWeight: 600 }}
      >
          Add Action
        </Button>
      </Box>
    </Paper>
  );
};

export default RuleCard;
